import styled, { css } from "styled-components";
import type { Property } from "csstype";

import { Image as ImageComponent } from "@components/DataDisplay/Image";

import { transition } from "@ui/helpers";
import { Text } from "@ui/Atoms/Text";

import { RootProps } from "./BentoBox.types";
import { getVariant } from "./BentoBox.getVariant";

export const Root = styled.article<RootProps>`
  --bento-box-padding: ${props => props.theme.spacing.spacing_24};

  ${props => getVariant(props.$variant)}

  position: relative;
  height: var(--bento-box-height);
  padding: ${props => props.theme.spacing.spacing_24};
  color: ${props => props.theme.palette.white};
  background-color: ${props => props.theme.palette.green600};
  border-radius: 12px;
  overflow: hidden;

  ${props =>
    props.$color === "black" &&
    css`
      color: ${props.theme.palette.grey900};
    `}

  ${props =>
    !!props.$backgroundColor &&
    css`
      background-color: ${props.theme.palette[props.$backgroundColor]};
    `}

  ${props => props.theme.breakpointsV2.smallDesktop} {
    padding: ${props => props.theme.spacing.spacing_32};
  }
`;

export const Body = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${props => props.theme.spacing.spacing_8};
  z-index: 1;
`;

export const AnchorOverlay = styled.a`
  position: absolute;
  inset: 0;
  opacity: 0;
  z-index: 3;
`;

export const Label = styled(Text).attrs({
  as: "span",
  $style: "semibold_14",
  $allUnset: true,
  $color: "white",
})`
  background-color: ${props => props.theme.palette.blue700};
  border-radius: 8px;
  width: fit-content;
  padding: ${props => props.theme.spacing.spacing_2}
    ${props => props.theme.spacing.spacing_8};
`;

export const Heading = styled(Text).attrs({
  as: "h3",
  $style: {
    default: "bold_24",
    smallTablet: "bold_28",
    smallDesktop: "bold_32",
  },
  $allUnset: true,
})``;

export const Description = styled(Text).attrs({
  as: "p",
  $style: {
    default: "regular_16",
    smallDesktop: "regular_18",
  },
  $allUnset: true,
})``;

export const CTA = styled(Text).attrs({
  as: "button",
  type: "button",
  $style: "semibold_18",
  $allUnset: true,
})`
  padding: ${({ theme }) =>
    `${theme.spacing.spacing_8} ${theme.spacing.spacing_2}`};
`;

export const BackgroundContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transition: ${props =>
    transition("transform", props.theme.animations.durationLong, "ease-out")};
  z-index: 0;

  ${Root}:has(${AnchorOverlay}:hover) & {
    ${props => props.theme.breakpointsV2.smallTablet} {
      transform: scale(1.05);
    }
  }
`;

export const Image = styled(ImageComponent).attrs(props => ({
  objectFit: props.objectFit || "cover",
}))`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  img {
    object-position: bottom center;
  }
`;
